/**
 * ExploreSearch — prominent global search for the authenticated Explore
 * page. Books are live Open Library results (debounced, via
 * useBookQuickSearch); People/Communities stay local mock data since Open
 * Library has no equivalent for either. Submitting hands the query up to
 * Explore.jsx, which swaps in the full paginated BookSearchResults view.
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, BookOpen, Users, User as UserIcon, X, ArrowRight } from "lucide-react";
import { cx } from "@/components/site/primitives";
import { toast } from "@/components/ui/sonner";
import { useBookQuickSearch } from "@/hooks/useBookSearch";
import { bookHref } from "@/lib/bookModel";
import { COMMUNITIES, PEOPLE_TO_FOLLOW } from "@/data";

function matches(text, query) {
  return text.toLowerCase().includes(query.toLowerCase());
}

export default function ExploreSearch({ onViewAllResults, focusRequested = false }) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const { books, numFound, isLoading: booksLoading } = useBookQuickSearch(query);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (focusRequested) inputRef.current?.focus();
  }, [focusRequested]);

  const trimmed = query.trim();
  const communities = trimmed ? COMMUNITIES.filter((c) => matches(c.name, trimmed)).slice(0, 3) : [];
  const people = trimmed ? PEOPLE_TO_FOLLOW.filter((p) => matches(p.name, trimmed) || matches(p.handle, trimmed)).slice(0, 3) : [];
  const hasResults = books.length > 0 || communities.length > 0 || people.length > 0;

  const submitSearch = () => {
    if (!trimmed) return;
    setIsOpen(false);
    onViewAllResults?.(trimmed);
  };

  return (
    <div ref={containerRef} className="relative mx-auto w-full max-w-2xl" data-testid="explore-search">
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter") submitSearch(); }}
          placeholder="Search books, authors, readers & more…"
          data-testid="explore-search-input"
          className="w-full rounded-full border border-line bg-card py-3.5 pl-11 pr-11 text-sm text-ink shadow-soft outline-none transition-colors placeholder:text-subtle/70 focus:border-coral"
        />
        {query && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => { setQuery(""); setIsOpen(false); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-subtle hover:text-coral"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <AnimatePresence>
        {isOpen && trimmed && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 right-0 top-full z-30 mt-2 max-h-[70vh] overflow-y-auto rounded-2xl border border-line bg-card p-3 text-left shadow-lift"
            data-testid="explore-search-results"
          >
            {booksLoading && books.length === 0 ? (
              <div className="space-y-2 p-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-9 animate-pulse rounded-xl bg-cream" />
                ))}
              </div>
            ) : !hasResults ? (
              <p className="px-3 py-4 text-sm text-subtle">No results for “{query}” yet.</p>
            ) : (
              <>
                {books.length > 0 && (
                  <ResultGroup icon={BookOpen} label="Books">
                    {books.map((b) => (
                      <ResultRow
                        key={b.id}
                        title={b.title}
                        sub={b.authors?.[0] ?? ''}
                        onClick={() => { navigate(bookHref(b), { state: { hint: b } }); setIsOpen(false); }}
                      />
                    ))}
                    {numFound > books.length && (
                      <button
                        type="button"
                        onClick={submitSearch}
                        data-testid="explore-search-view-all"
                        className="mt-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-semibold text-coral hover:bg-coral/5"
                      >
                        View all {numFound.toLocaleString()} results <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </ResultGroup>
                )}
                {communities.length > 0 && (
                  <ResultGroup icon={Users} label="Communities">
                    {communities.map((c) => (
                      <ResultRow key={c.name} title={c.name} sub={`${c.members} members`} onClick={() => { toast(`Opening ${c.name}…`); setIsOpen(false); }} />
                    ))}
                  </ResultGroup>
                )}
                {people.length > 0 && (
                  <ResultGroup icon={UserIcon} label="People">
                    {people.map((p) => (
                      <ResultRow key={p.handle} title={p.name} sub={`@${p.handle}`} onClick={() => { toast(`Opening @${p.handle}…`); setIsOpen(false); }} />
                    ))}
                  </ResultGroup>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ResultGroup({ icon: Icon, label, children }) {
  return (
    <div className="mb-1 last:mb-0">
      <p className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-subtle">
        <Icon className="h-3 w-3" /> {label}
      </p>
      {children}
    </div>
  );
}

function ResultRow({ title, sub, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx("flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left transition-colors hover:bg-cream")}
    >
      <span className="truncate text-sm font-medium text-ink">{title}</span>
      <span className="shrink-0 truncate text-xs text-subtle">{sub}</span>
    </button>
  );
}
